import {
  Activity,
  Bot,
  Boxes,
  Check,
  ChevronDown,
  Cloud,
  Code2,
  CodeXml,
  ExternalLink,
  Files,
  GitFork,
  History,
  LayoutPanelTop,
  PanelRight,
  Play,
  Redo2,
  RotateCcw,
  Sparkles,
  Undo2,
} from 'lucide-react'
import type { AgentPhase } from '../agent/machine'

export type SidePanelTab = 'inspect' | 'versions' | 'performance'
export type WorkspaceMode = 'design' | 'split' | 'code'

interface TopBarProps {
  branchName: string
  canUndo: boolean
  canRedo: boolean
  onUndo: () => void
  onRedo: () => void
  onReset: () => void
  onRemix: () => void
  onPreview: () => void
  onBranchClick: () => void
}

export function TopBar({
  branchName,
  canUndo,
  canRedo,
  onUndo,
  onRedo,
  onReset,
  onRemix,
  onPreview,
  onBranchClick,
}: TopBarProps) {
  return (
    <header className="top-bar">
      <div className="brand-lockup">
        <span className="brand-glyph"><Boxes size={17} /></span>
        <strong>Driftless</strong>
        <span className="beta-badge">LAB</span>
      </div>
      <div className="project-crumbs">
        <span>Northstar launch</span><i>/</i>
        <button type="button" onClick={onBranchClick}><GitFork size={11} /> {branchName}<ChevronDown size={10} /></button>
      </div>
      <div className="history-controls">
        <button type="button" aria-label="Undo" disabled={!canUndo} onClick={onUndo}><Undo2 size={14} /></button>
        <button type="button" aria-label="Redo" disabled={!canRedo} onClick={onRedo}><Redo2 size={14} /></button>
        <button type="button" aria-label="Reset demo" onClick={onReset}><RotateCcw size={13} /></button>
      </div>
      <div className="top-spacer" />
      <div className="sync-pill"><Cloud size={12} /><span>Synced</span><i>0 conflicts</i></div>
      <div className="collaborators" aria-label="Collaborators online"><span>M</span><span>J</span><span>Y</span></div>
      <button className="remix-button" type="button" onClick={onRemix}><GitFork size={12} /> Remix</button>
      <a className="github-button" href="https://github.com/Redchar1992/driftless-studio" target="_blank" rel="noreferrer" aria-label="Open GitHub repository"><CodeXml size={14} /><ExternalLink size={9} /></a>
      <button className="run-button" type="button" onClick={onPreview}><Play size={11} fill="currentColor" /> Preview</button>
    </header>
  )
}

interface ActivityRailProps {
  activeTab: SidePanelTab
  agentOpen: boolean
  onTabChange: (tab: SidePanelTab) => void
  onAgentToggle: () => void
}

export function ActivityRail({ activeTab, agentOpen, onTabChange, onAgentToggle }: ActivityRailProps) {
  return (
    <nav className="activity-rail" aria-label="Workspace tools">
      <button type="button" className="is-active" aria-label="Explorer"><Files size={17} /></button>
      <button type="button" className={activeTab === 'inspect' ? 'is-context' : ''} aria-label="Inspector" onClick={() => onTabChange('inspect')}><PanelRight size={17} /></button>
      <button type="button" className={agentOpen ? 'is-context agent-rail-button' : 'agent-rail-button'} aria-label="AI agent" onClick={onAgentToggle}><Sparkles size={17} /><i /></button>
      <button type="button" className={activeTab === 'versions' ? 'is-context' : ''} aria-label="Version history" onClick={() => onTabChange('versions')}><History size={17} /></button>
      <button type="button" className={activeTab === 'performance' ? 'is-context' : ''} aria-label="Performance" onClick={() => onTabChange('performance')}><Activity size={17} /></button>
      <span className="rail-spacer" />
      <button type="button" aria-label="Components"><LayoutPanelTop size={17} /></button>
    </nav>
  )
}

interface WorkspaceToolbarProps {
  mode: WorkspaceMode
  agentOpen: boolean
  nodeCount: number
  onModeChange: (mode: WorkspaceMode) => void
  onAgentToggle: () => void
}

export function WorkspaceToolbar({ mode, agentOpen, nodeCount, onModeChange, onAgentToggle }: WorkspaceToolbarProps) {
  return (
    <div className="workspace-toolbar">
      <div className="mode-switch" role="group" aria-label="Workspace view">
        <button className={mode === 'design' ? 'is-active' : ''} type="button" onClick={() => onModeChange('design')}><LayoutPanelTop size={12} /> Design</button>
        <button className={mode === 'split' ? 'is-active' : ''} type="button" onClick={() => onModeChange('split')}><PanelRight size={12} /> Split</button>
        <button className={mode === 'code' ? 'is-active' : ''} type="button" onClick={() => onModeChange('code')}><Code2 size={12} /> Code</button>
      </div>
      <div className="graph-status"><span><i /> Shared graph</span><b>{nodeCount} stable nodes</b></div>
      <button className={`agent-toggle ${agentOpen ? 'is-active' : ''}`} type="button" onClick={onAgentToggle}><Bot size={13} /> Agent <kbd>⌘K</kbd></button>
    </div>
  )
}

interface RightPanelHeaderProps {
  activeTab: SidePanelTab
  onTabChange: (tab: SidePanelTab) => void
}

export function RightPanelHeader({ activeTab, onTabChange }: RightPanelHeaderProps) {
  return (
    <div className="right-panel-tabs" role="tablist" aria-label="Details panel">
      <button role="tab" type="button" aria-selected={activeTab === 'inspect'} className={activeTab === 'inspect' ? 'is-active' : ''} onClick={() => onTabChange('inspect')}>Inspect</button>
      <button role="tab" type="button" aria-selected={activeTab === 'versions'} className={activeTab === 'versions' ? 'is-active' : ''} onClick={() => onTabChange('versions')}>Versions</button>
      <button role="tab" type="button" aria-selected={activeTab === 'performance'} className={activeTab === 'performance' ? 'is-active' : ''} onClick={() => onTabChange('performance')}>Perf</button>
    </div>
  )
}

interface StatusBarProps {
  renderMs: number
  nodeCount: number
  codeError: string | null
  agentPhase: AgentPhase
}

export function StatusBar({ renderMs, nodeCount, codeError, agentPhase }: StatusBarProps) {
  return (
    <footer className="status-bar">
      <span className={codeError ? 'status-error' : ''}>{codeError ? 'AST guard active' : <><Check size={10} /> Graph valid</>}</span>
      <span>React 19 · TypeScript</span>
      <span>{nodeCount} nodes</span>
      <span className="status-spacer" />
      {agentPhase !== 'idle' ? <span><Sparkles size={10} /> Agent {agentPhase}</span> : null}
      <span>Preview {renderMs.toFixed(1)} ms</span>
      <span><Activity size={10} /> 60 fps</span>
    </footer>
  )
}
